/**
 * Declarative sub-navigation for agent context (paths must match vue-router).
 * Primary/secondary sidebar items live in sidebarLinks.js; this file adds
 * nested items that are not in the main array (e.g. Contacts tabs, Settings).
 */

import { FEATURES } from '@/access/features';
import { PERMISSIONS } from '@/access/permissions';
import { ROLES } from '@/access/roles';

/** @typedef {{ label: string, path: string, routeName?: string, feature?: string, permission?: string, permissions?: string[], roles?: string[], hidden?: boolean }} NavChild */

/** @type {NavChild[]} */
export const CONTACTS_SUBNAV = [
  {
    label: 'All Contacts',
    path: '/contacts',
    routeName: 'contacts-main',
  },
  {
    label: 'Views',
    path: '/contacts/views',
    routeName: 'contacts-views',
  },
  {
    label: 'Archived',
    path: '/contacts/archived',
    routeName: 'contacts-archived',
    feature: FEATURES.CONTACTS_ARCHIVED,
  },
  {
    label: 'Notes',
    path: '/contacts/notes',
    routeName: 'contacts-notes',
    feature: FEATURES.CONTACTS_NOTES,
  },
  {
    label: 'Tasks',
    path: '/contacts/tasks',
    routeName: 'contacts-tasks',
    feature: FEATURES.CONTACTS_TASKS,
  },
  {
    label: 'Relationships',
    path: '/contacts/relationships',
    routeName: 'contacts-relationships',
    feature: FEATURES.CONTACTS_RELATIONSHIPS,
  },
  {
    label: 'Boards',
    path: '/contacts/boards',
    routeName: 'contacts-boards',
    feature: FEATURES.CONTACTS_BOARDS,
  },
  {
    label: 'Import contacts',
    path: '/contacts/import',
    routeName: 'contacts-import',
  },
  {
    label: 'New contact',
    path: '/contacts/create',
    routeName: 'contact-create',
  },
];

/** @type {NavChild[]} */
export const GROUPS_SUBNAV = [
  {
    label: 'All groups',
    path: '/groups',
    routeName: 'groups',
  },
  {
    label: 'New group',
    path: '/groups/create',
    routeName: 'group-create',
  },
];

/**
 * Settings sidebar (matches Settings.vue navLinks + otherSettingLinks paths).
 * @type {NavChild[]}
 */
export const SETTINGS_SUBNAV = [
  {
    label: 'Profile',
    path: '/settings/profile',
    routeName: 'settings-profile',
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Users',
    path: '/settings/users',
    routeName: 'settings-users',
    feature: FEATURES.USERS,
    permission: PERMISSIONS.READ_USERS,
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Archived Users',
    path: '/settings/archived-users',
    routeName: 'settings-archived-users',
    feature: FEATURES.ARCHIVED_USERS,
    permission: PERMISSIONS.READ_USERS,
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'User Permissions',
    path: '/settings/permissions',
    routeName: 'settings-permissions',
    permission: PERMISSIONS.UPDATE_USERS,
    feature: FEATURES.USER_PERMISSIONS,
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Teams',
    path: '/settings/teams',
    routeName: 'settings-teams',
    feature: FEATURES.TEAMS,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Tags',
    path: '/settings/tags',
    routeName: 'settings-tags',
    feature: FEATURES.TAGS,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Fields',
    path: '/settings/fields',
    routeName: 'settings-fields',
    feature: FEATURES.FIELDS,
    permissions: [PERMISSIONS.CREATE_FIELDS, PERMISSIONS.EDIT_FIELDS],
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Account',
    path: '/settings/account',
    routeName: 'settings-account',
    feature: FEATURES.ACCOUNT,
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Integrations',
    path: '/settings/integrations',
    routeName: 'settings-integrations',
    feature: FEATURES.INTEGRATIONS,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'API',
    path: '/settings/api-access',
    routeName: 'settings-api-access',
    feature: FEATURES.INTEGRATIONS,
    permission: PERMISSIONS.HOOKS,
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Portal Settings',
    path: '/settings/portal',
    routeName: 'settings-portal',
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Links',
    path: '/settings/links',
    routeName: 'settings-links',
    permission: PERMISSIONS.LINKS,
    feature: FEATURES.LINKS,
    roles: [ROLES.ADMIN, ROLES.GUEST],
  },
  {
    label: 'Backend',
    path: '/settings/backend',
    routeName: 'settings-backend',
    hidden: true,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Compliance',
    path: '/settings/compliance',
    routeName: 'settings-compliance',
    hidden: true,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Business Information',
    path: '/settings/business',
    routeName: 'settings-business',
    feature: FEATURES.OTHER_SETTINGS,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Email Templates',
    path: '/settings/email-templates',
    routeName: 'settings-email-templates',
    feature: FEATURES.EMAIL_TEMPLATES,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Email Addresses',
    path: '/settings/email-addresses',
    routeName: 'settings-email-addresses',
    feature: FEATURES.EMAIL_ADDRESSES,
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Conversations',
    path: '/settings/conversations',
    routeName: 'settings-conversations',
    roles: [ROLES.ADMIN],
  },
];
